import React, { useState, useEffect, useRef } from 'react';
import { Check, ArrowRight, ArrowLeft, Coins, Clock, Plus, Loader2, AlertCircle, Crown, X, MapPin, Mail } from 'lucide-react';
import { QRCodeCanvas } from 'qrcode.react';
import DraggablePopularTag from './DraggablePopularTag';
import { useLanguage, translations } from '../i18nContext';
import {
  purchaseMembership,
  purchaseDurationPackage,
  confirmMembershipPurchase,
  confirmDurationPackagePurchase,
  getMembershipInfo,
  getPaymentRegion,
  PaymentRegionInfo,
} from '../services/membership';
import { MembershipInfo } from '../types';
import { copyToClipboard } from '../utils/clipboard';
import { getErrorMessage } from '../utils/errorUtils';

/** Allowed Alipay payment form domains */
const ALIPAY_DOMAIN_RE = /^https:\/\/(open|openapi)\.alipay(dev)?\.com\//;

/** WeChat Pay SVG icon */
function WeChatPayIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
      <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.17 4.203 3.002 5.55a.59.59 0 01.213.665l-.39 1.48c-.019.07-.048.141-.048.213 0 .163.13.295.29.295a.326.326 0 00.167-.054l1.903-1.114a.864.864 0 01.717-.098 10.16 10.16 0 002.837.403c.276 0 .543-.027.811-.05a6.127 6.127 0 01-.253-1.736c0-3.74 3.568-6.768 7.967-6.768.386 0 .762.03 1.133.073C16.318 4.632 12.87 2.188 8.691 2.188zm-2.87 4.401a1.026 1.026 0 11-.001 2.051 1.026 1.026 0 010-2.051zm5.742 0a1.026 1.026 0 110 2.052 1.026 1.026 0 010-2.052z" fill="#07C160"/>
      <path d="M23.997 15.437c0-3.199-3.19-5.79-7.118-5.79-3.927 0-7.115 2.591-7.115 5.79 0 3.2 3.188 5.791 7.115 5.791a8.451 8.451 0 002.351-.336.72.72 0 01.594.082l1.57.917a.27.27 0 00.138.045.24.24 0 00.24-.243c0-.06-.023-.118-.039-.176l-.326-1.215a.49.49 0 01.176-.548c1.528-1.12 2.414-2.764 2.414-4.317zm-9.652-.838a.853.853 0 110-1.706.853.853 0 010 1.706zm5.068 0a.853.853 0 110-1.706.853.853 0 010 1.706z" fill="#07C160"/>
    </svg>
  );
}

/** Alipay SVG icon */
function AlipayIcon({ size = 24 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 1024 1024" fill="none">
      <path d="M1024.0512 701.0304V196.864A196.9664 196.9664 0 0 0 827.136 0H196.864A196.9664 196.9664 0 0 0 0 196.864v630.272A196.9152 196.9152 0 0 0 196.864 1024h630.272a197.12 197.12 0 0 0 193.8432-162.0992c-52.224-22.6304-278.528-120.32-396.4416-176.64-89.7024 108.6976-183.7056 173.9264-325.3248 173.9264s-236.1856-87.2448-224.8192-194.048c7.4752-70.0416 55.552-184.576 264.2944-164.9664 110.08 10.3424 160.4096 30.8736 250.1632 60.5184 23.1936-42.5984 42.496-89.4464 57.1392-139.264H248.064v-39.424h196.9152V311.1424H204.8V267.776h240.128V165.632s2.1504-15.9744 19.8144-15.9744h98.4576V267.776h256v43.4176h-256V381.952h208.8448a805.9904 805.9904 0 0 1-84.8384 212.6848c60.672 22.016 336.7936 106.3936 336.7936 106.3936zM283.5456 791.6032c-149.6576 0-173.312-94.464-165.376-133.9392 7.8336-39.3216 51.2-90.624 134.4-90.624 95.5904 0 181.248 24.4736 284.0576 74.5472-72.192 94.0032-160.9216 150.016-253.0816 150.016z" fill="#009FE8"/>
    </svg>
  );
}

interface UpgradePageProps {
  onBack: () => void;
  mode?: 'upgrade' | 'welcome';
}

export default function UpgradePage({ onBack, mode = 'upgrade' }: UpgradePageProps) {
  const isWelcome = mode === 'welcome';
  const [isAnnual, setIsAnnual] = useState(true);
  const qrCanvasRef = useRef<HTMLCanvasElement>(null);
  const { t, language } = useLanguage();

  // 购买状态
  const [purchasing, setPurchasing] = useState<string | null>(null);
  const [purchaseError, setPurchaseError] = useState<string | null>(null);
  const [purchaseSuccess, setPurchaseSuccess] = useState<string | null>(null);
  const [membershipInfo, setMembershipInfo] = useState<MembershipInfo | null>(null);

  // 地区检测弹窗状态
  const [showRegionModal, setShowRegionModal] = useState(false);
  const [regionInfo, setRegionInfo] = useState<PaymentRegionInfo | null>(null);
  const [regionLoading, setRegionLoading] = useState(false);
  const [pendingPurchase, setPendingPurchase] = useState<{ type: 'membership' | 'duration'; value: string } | null>(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);

  // 二维码弹窗状态
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string | null>(null);
  const [qrOrderNo, setQrOrderNo] = useState<string | null>(null);
  const [qrPaymentType, setQrPaymentType] = useState<string | null>(null);

  // 支付信息提示（非成功确认，仅跳转/待确认提示）
  const [purchaseInfo, setPurchaseInfo] = useState<string | null>(null);

  // Team联系弹窗状态
  const [showContactModal, setShowContactModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // 价格地区状态（根据IP检测，独立于UI语言）
  const [paymentRegion, setPaymentRegion] = useState<string>('CN');

  const isCofounder = membershipInfo?.isCofounder === true;
  const cofounderDiscount = membershipInfo?.cofounderDiscount || 0;
  const discountMultiplier = isCofounder ? (100 - cofounderDiscount) / 100 : 1;

  const applyDiscount = (price: number | string): string => {
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num) || !isCofounder) return String(price);
    return (num * discountMultiplier).toFixed(0);
  };

  // 根据IP地区选择价格和币种（独立于UI语言）
  const regionPricing = paymentRegion === 'OVERSEAS' ? translations.en.pricing : translations.zh.pricing;
  const regionPrices = regionPricing.prices;
  const regionCurrency = regionPricing.currencySymbol;

  // 加载会员信息 + 检测IP地区（L-1: 缓存结果，避免重复请求）
  const cachedRegionRef = useRef<PaymentRegionInfo | null>(null);
  useEffect(() => {
    loadMembershipInfo();
    getPaymentRegion().then(info => {
      setPaymentRegion(info.region);
      cachedRegionRef.current = info;
    }).catch(() => {});
  }, []);

  const loadMembershipInfo = async () => {
    try {
      const info = await getMembershipInfo();
      setMembershipInfo(info);
    } catch (err) {
      // 未登录或获取失败，忽略
    }
  };

  // 点击购买时先检测地区（L-1: 使用缓存，仅首次无缓存时请求）
  const handlePurchaseClick = async (type: 'membership' | 'duration', value: string) => {
    setPendingPurchase({ type, value });
    setShowRegionModal(true);
    if (cachedRegionRef.current) {
      setRegionInfo(cachedRegionRef.current);
      setRegionLoading(false);
    } else {
      setRegionLoading(true);
      try {
        const info = await getPaymentRegion();
        setRegionInfo(info);
        cachedRegionRef.current = info;
      } catch {
        setRegionInfo(null);
      } finally {
        setRegionLoading(false);
      }
    }
  };

  // 弹窗确认后执行真正的购买
  const handleRegionConfirm = () => {
    if (!selectedPaymentMethod) return;
    setShowRegionModal(false);
    if (!pendingPurchase) return;
    const method = selectedPaymentMethod;
    setSelectedPaymentMethod(null);
    if (pendingPurchase.type === 'membership') {
      handlePurchaseMembership(pendingPurchase.value as 'pro' | 'team', method);
    } else {
      handlePurchaseDurationPackage(pendingPurchase.value, method);
    }
    setPendingPurchase(null);
  };

  // 购买会员
  const handlePurchaseMembership = async (membershipType: 'pro' | 'team', paymentMethod: string) => {
    setPurchasing(membershipType);
    setPurchaseError(null);
    setPurchaseSuccess(null);

    try {
      const months = isAnnual ? 12 : 1;
      const response = await purchaseMembership({
        membershipType,
        months,
        isAnnual,
        paymentMethod,
      });

      if (response.codeUrl) {
        setQrCodeUrl(response.codeUrl);
        setQrOrderNo(response.orderNo);
        setQrPaymentType(response.paymentType || null);
        setShowQrModal(true);
      } else if (response.payForm) {
        // H-1: DOMParser + 域名白名单，替代 innerHTML 注入
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.payForm, 'text/html');
        const form = doc.querySelector('form');
        if (!form || !ALIPAY_DOMAIN_RE.test(form.action)) {
          throw new Error('Invalid payment form');
        }
        const container = document.createElement('div');
        container.appendChild(document.adoptNode(form));
        document.body.appendChild(container);
        form.setAttribute('target', '_blank');
        form.submit();
        document.body.removeChild(container);
        // H-3: info 级别提示（非 success），不做成功确认
        setPurchaseInfo(t.paymentModal.alipayRedirectPending);
        await loadMembershipInfo();
      } else {
        await confirmMembershipPurchase(response.orderNo);
        setPurchaseSuccess(
          membershipType === 'pro' ? t.paymentModal.purchaseSuccessPro : t.paymentModal.purchaseSuccessTeam
        );
        await loadMembershipInfo();
      }
    } catch (err: unknown) {
      setPurchaseError(getErrorMessage(err, t.paymentModal.purchaseFailedFallback));
    } finally {
      setPurchasing(null);
    }
  };

  // 购买时长包
  const handlePurchaseDurationPackage = async (packageCode: string, paymentMethod: string) => {
    setPurchasing(packageCode);
    setPurchaseError(null);
    setPurchaseSuccess(null);

    try {
      const response = await purchaseDurationPackage({ packageCode, paymentMethod });

      if (response.codeUrl) {
        setQrCodeUrl(response.codeUrl);
        setQrOrderNo(response.orderNo);
        setQrPaymentType(response.paymentType || null);
        setShowQrModal(true);
      } else if (response.payForm) {
        // H-1: DOMParser + 域名白名单
        const parser = new DOMParser();
        const doc = parser.parseFromString(response.payForm, 'text/html');
        const form = doc.querySelector('form');
        if (!form || !ALIPAY_DOMAIN_RE.test(form.action)) {
          throw new Error('Invalid payment form');
        }
        const container = document.createElement('div');
        container.appendChild(document.adoptNode(form));
        document.body.appendChild(container);
        form.setAttribute('target', '_blank');
        form.submit();
        document.body.removeChild(container);
        // H-3: info 级别提示
        setPurchaseInfo(t.paymentModal.alipayRedirectPending);
        await loadMembershipInfo();
      } else {
        await confirmDurationPackagePurchase(response.orderNo);
        setPurchaseSuccess(t.paymentModal.purchaseSuccessDuration);
        await loadMembershipInfo();
      }
    } catch (err: unknown) {
      setPurchaseError(getErrorMessage(err, t.paymentModal.purchaseFailedFallback));
    } finally {
      setPurchasing(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 isolate">
      {/* Blurred Backdrop */}
      <div
        className="absolute inset-0 bg-white/35 backdrop-blur-[8px] transform-gpu pointer-events-auto transition-none"
        style={{ backfaceVisibility: 'hidden', perspective: 1000 }}
        onClick={onBack}
      >
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(255,255,255,0.25)_100%)] pointer-events-none"></div>
      </div>

      {/* Modal Container */}
      <div className={`relative w-full ${isWelcome ? 'max-w-6xl' : 'max-w-7xl'} max-h-[90vh] bg-surface rounded-pricing shadow-modal-heavy border border-surface-border animate-in zoom-in-95 fade-in duration-500 z-10 flex flex-col overflow-hidden`}>
        {/* 顶部区域 */}
        {isWelcome ? (
          <>
            <button
              onClick={onBack}
              className="absolute top-6 right-6 z-20 p-2.5 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={22} />
            </button>
          </>
        ) : (
        <div className="shrink-0 z-50 bg-white/80 backdrop-blur-xl border-b border-surface-border rounded-t-pricing">
          <div className="max-w-7xl mx-auto px-8 py-3 flex items-center">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-ink-muted hover:text-brand-900 transition-colors text-sm font-medium"
            >
              <ArrowLeft size={16} />
              <span>{t.paymentModal.back}</span>
            </button>
            <div className="flex-1 flex items-center justify-center">
              <h1 className="text-[20px] font-serif font-bold text-brand-900">
                {t.paymentModal.upgradePlan}
              </h1>
            </div>
            <button
              onClick={onBack}
              className="p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>
          </div>
          {membershipInfo && (
            <div className="max-w-7xl mx-auto px-8 pb-3 flex items-center justify-center gap-2">
              <span className="text-[11px] font-bold text-ink-muted uppercase tracking-wider">{t.paymentModal.currentPlanLabel}:</span>
              <div className="font-serif font-black text-[13px] tracking-tight flex items-center">
                {membershipInfo.userLevel === 'team' ? (
                  <span className="text-prestige-500 drop-shadow-sm flex items-center gap-1">
                    <Crown size={11} fill="currentColor" />
                    Team <span className="italic font-bold ml-0.5 opacity-80">Plan</span>
                  </span>
                ) : membershipInfo.userLevel === 'pro' ? (
                  <span className="text-prestige-500 drop-shadow-sm">
                    Pro <span className="italic font-bold ml-0.5 opacity-80">Plan</span>
                  </span>
                ) : (
                  <span className="text-brand-900">
                    Free <span className="italic text-brand-900/60 font-bold ml-0">Plan</span>
                  </span>
                )}
              </div>
              {isCofounder && (
                <span className="cofounder-badge relative flex items-center gap-1 pl-2 pr-2.5 py-0.5 rounded-lg border border-amber-300/70 overflow-hidden">
                  <div className="absolute top-0 left-1 w-5 h-5 rounded-full bg-amber-400/30 blur-[4px] cofounder-bubble-1 pointer-events-none"></div>
                  <div className="absolute bottom-0 right-1 w-4 h-4 rounded-full bg-yellow-500/25 blur-[4px] cofounder-bubble-2 pointer-events-none"></div>
                  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" className="shrink-0 relative z-10 text-amber-700" style={{filter: 'drop-shadow(0 0 2px rgba(180,120,20,0.4))'}}>
                    <path d="M12 5l1.8 5h5.2l-4.2 3 1.6 4.8L12 15l-4.4 2.8 1.6-4.8L5 10h5.2L12 5z" fill="currentColor" opacity="0.85"/>
                  </svg>
                  <span className="relative z-10 text-[8px] font-black uppercase tracking-[0.12em] bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-800 bg-clip-text text-transparent leading-none">
                    Co-founder
                  </span>
                </span>
              )}
            </div>
          )}
        </div>
        )}

        <div className="flex-1 overflow-y-auto">
        <div className={`${isWelcome ? 'px-8 md:px-12 pt-10 pb-12' : 'px-8 py-5'} relative z-10`}>

        {/* Welcome Header (welcome mode only) */}
        {isWelcome && (
          <div className="text-center mb-10">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-brand-900 mb-3 tracking-tight">
              {language === 'zh' ? '欢迎加入 Minutes AI' : 'Welcome to Minutes AI'}
            </h2>
            <p className="text-ink-secondary text-base font-medium opacity-70 max-w-lg mx-auto">
              {language === 'zh' ? '选择适合你的方案，开始高效会议之旅' : 'Choose the plan that fits you and start your productive meeting journey'}
            </p>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="text-center mb-5">
          <div className="flex items-center justify-center">
            <button
              type="button"
              aria-label={isAnnual ? t.pricing.yearly : t.pricing.monthly}
              className="relative inline-flex h-11 w-full max-w-[360px] cursor-pointer rounded-full border border-surface-divider bg-white/80 p-1 shadow-nav backdrop-blur-xl"
              onClick={() => setIsAnnual(!isAnnual)}
            >
              <div
                className={`absolute top-1 bottom-1 rounded-full bg-brand-900 transition-all duration-500 ease-[cubic-bezier(0.175,0.885,0.32,1.275)] shadow-lg ${isAnnual ? 'left-[calc(36%+5px)] right-1' : 'left-1 right-[calc(64%+5px)]'}`}
              ></div>

              <div className={`relative z-10 flex w-[36%] items-center justify-center text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 ${!isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                {t.pricing.monthly}
              </div>

              <div className={`relative z-10 flex w-[64%] items-center justify-center gap-1.5 text-[10px] font-bold uppercase tracking-widest transition-colors duration-300 ${isAnnual ? 'text-white' : 'text-ink-dark'}`}>
                {t.pricing.yearly}
                <span className={`relative overflow-hidden rounded-full px-2 py-0.5 text-[9px] font-black tracking-[0.1em] transition-all duration-300 shadow-sm ${isAnnual ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-200/50' : 'bg-surface text-ink-muted border border-surface-border'}`}>
                  <span className="relative z-10">{t.pricing.save20 || '-20%'}</span>
                  {isAnnual && <div className="absolute inset-0 bg-gradient-to-r from-transparent via-brand-200/40 to-transparent animate-shimmer-gold opacity-50 pointer-events-none w-[200%] h-full"></div>}
                </span>
              </div>
            </button>
          </div>
        </div>

        {/* Pricing Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl mx-auto items-stretch mb-8">

            {/* 1. Essential (Free) */}
            <div className="group flex flex-col rounded-pricing bg-white/90 backdrop-blur-sm border border-surface-border p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-card-hover">
                <div className="min-h-[90px] space-y-1.5">
                    <div className="text-[9px] font-black uppercase tracking-[0.2em] text-ink-muted/50">Essential</div>
                    <h3 className="text-2xl font-serif font-bold text-brand-900">{t.pricing.plans.basic.name}</h3>
                    <p className="max-w-[17rem] text-xs leading-relaxed text-ink-secondary font-bold opacity-80">{t.pricing.plans.basic.desc}</p>
                </div>

                <div className="min-h-[80px] py-1 flex flex-col justify-start">
                    <div className="flex items-baseline gap-1">
                        <span className="text-lg font-serif text-brand-900/35">{regionCurrency}</span>
                        <span className="text-4xl font-serif font-bold text-brand-900">{regionPrices.free}</span>
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-ink-muted">/mo</span>
                    </div>
                </div>

                <div className="mb-5 flex-1 border-t border-surface-divider pt-4">
                    <ul className="space-y-2.5">
                        {t.pricing.features.basic.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-xs font-medium text-brand-900 leading-relaxed">
                                <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-surface text-ink-muted transition-colors group-hover:bg-surface-hover group-hover:text-brand-900">
                                    <Check size={10} strokeWidth={3} />
                                </div>
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="mt-auto border-t border-surface-divider pt-4">
                    <button
                      disabled={membershipInfo?.userLevel === 'free'}
                      className={`h-11 w-full rounded-xl border text-xs font-bold transition-all active:scale-[0.98] ${
                        membershipInfo?.userLevel === 'free'
                          ? 'bg-surface-border text-ink-secondary border-surface-border cursor-default'
                          : 'border-surface-border bg-surface text-brand-900 hover:bg-white hover:shadow-md'
                      }`}
                    >
                        {membershipInfo?.userLevel === 'free'
                          ? t.paymentModal.currentPlan
                          : t.pricing.getStarted}
                    </button>
                </div>
            </div>

            {/* 2. Professional (Pro) */}
            <div className="relative z-10 flex flex-col overflow-visible rounded-pricing border border-brand-300/30 bg-white/95 p-6 shadow-card-hover ring-1 ring-brand-200/40 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1">
                <div className="absolute inset-0 rounded-pricing bg-gradient-to-b from-brand-50/70 via-white/90 to-prestige-50/40 pointer-events-none"></div>
                <DraggablePopularTag label={t.pricing.mostPopular} className="top-4 right-6" />

                <div className="relative min-h-[90px] pr-8 space-y-1.5">
                    <div className="text-[9px] font-black uppercase tracking-[0.2em] text-brand-700/40">Professional</div>
                    <h3 className="text-2xl font-serif font-bold text-brand-900">{t.pricing.plans.pro.name}</h3>
                    <p className="max-w-[17rem] text-xs leading-relaxed text-brand-700 font-bold opacity-80">{t.pricing.plans.pro.desc}</p>
                </div>

                <div className="relative min-h-[80px] py-1 flex flex-col justify-start gap-1.5 text-brand-900">
                    <div className="flex items-baseline gap-1">
                        <span className="text-lg font-serif font-bold opacity-35">{regionCurrency}</span>
                        <span className="text-4xl font-serif font-bold tracking-tighter transition-all duration-500">{isCofounder ? applyDiscount(isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly) : (isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly)}</span>
                        <span className="ml-1 text-[10px] font-bold uppercase tracking-widest text-brand-700">/mo</span>
                    </div>
                    {isCofounder && (
                        <div className="flex items-center gap-2">
                            <span className="text-[10px] text-ink-muted/60 line-through font-semibold">{regionCurrency}{isAnnual ? regionPrices.pro.yearly : regionPrices.pro.monthly}</span>
                            {isAnnual && <span className="text-[10px] text-ink-muted/60 line-through font-semibold">{regionCurrency}{regionPrices.pro.monthly}</span>}
                            <span className="cofounder-badge relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-lg border border-amber-300/70 overflow-hidden">
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" className="shrink-0 text-amber-700"><path d="M12 5l1.8 5h5.2l-4.2 3 1.6 4.8L12 15l-4.4 2.8 1.6-4.8L5 10h5.2L12 5z" fill="currentColor" opacity="0.85"/></svg>
                              <span className="text-[7px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-800 bg-clip-text text-transparent">Co-founder -{cofounderDiscount}%</span>
                            </span>
                        </div>
                    )}
                    {!isCofounder && isAnnual && (
                        <div className="flex items-baseline gap-1.5 text-[10px] font-semibold tracking-tight leading-relaxed text-ink-muted/60">
                            <span className="line-through">{regionCurrency}{regionPrices.pro.monthly}</span>
                            <span>,</span>
                            <span>{t.pricing.annualSaveTag}</span>
                        </div>
                    )}
                </div>

                <div className="relative mb-5 flex-1 border-t border-brand-200/60 pt-4">
                    <ul className="space-y-2.5">
                        {t.pricing.features.pro.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-xs font-medium text-brand-900 leading-relaxed">
                                <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
                                    <Check size={10} strokeWidth={3} />
                                </div>
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative mt-auto border-t border-brand-200/60 pt-4">
                    <button
                      onClick={() => handlePurchaseClick('membership', 'pro')}
                      disabled={purchasing === 'pro'}
                      className="group/btn relative h-11 w-full overflow-hidden rounded-xl text-sm font-bold transition-all active:scale-[0.98] bg-brand-600 text-surface-light shadow-btn-green hover:-translate-y-0.5 hover:bg-brand-700"
                    >
                        <div className="absolute inset-0 bg-noise opacity-10 mix-blend-overlay"></div>
                        <span className="relative z-10 flex items-center justify-center gap-2">
                            {purchasing === 'pro' ? (
                              <>
                                <Loader2 size={16} className="animate-spin" />
                                {t.paymentModal.processing}
                              </>
                            ) : membershipInfo?.userLevel === 'pro' ? (
                              <>
                                {t.paymentModal.renew}
                                <ArrowRight size={16} className="transition-transform group-hover/btn:translate-x-1 stroke-[2.5]" />
                              </>
                            ) : (
                              <>
                                {t.pricing.startFree}
                                <ArrowRight size={16} className="transition-transform group-hover/btn:translate-x-1 stroke-[2.5]" />
                              </>
                            )}
                        </span>
                    </button>
                </div>
            </div>

            {/* 3. Executive (Team) */}
            <div className="group relative flex flex-col overflow-hidden rounded-pricing border border-ink-dark bg-prestige-900 p-6 text-surface-light shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_40px_80px_-20px_rgba(62,52,48,0.28)]">
                <div className="absolute top-0 right-0 h-48 w-48 rounded-bl-full bg-prestige-950/40 pointer-events-none"></div>
                <div className="absolute top-0 right-0 h-64 w-64 rounded-bl-[160px] bg-prestige-300/5 pointer-events-none"></div>
                <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>

                <div className="relative min-h-[90px] space-y-1.5">
                    <div className="text-[9px] font-black uppercase tracking-[0.2em] text-prestige-accent">Executive</div>
                    <h3 className="text-2xl font-serif font-bold tracking-tight">{t.pricing.plans.team.name}</h3>
                    <p className="max-w-[17rem] text-xs leading-relaxed text-white/70 font-bold">{t.pricing.plans.team.desc}</p>
                </div>

                <div className="relative min-h-[80px] py-1 flex flex-col justify-start gap-1.5">
                    <div className="flex items-start pt-1">
                        <span className="text-2xl font-serif font-bold tracking-tight text-prestige-accent leading-none">
                            {t.paymentModal.customPlan}
                        </span>
                    </div>
                    <p className="text-[10px] font-semibold leading-relaxed text-white/60 max-w-[16rem]">
                        {t.paymentModal.customPlanDesc}
                    </p>
                </div>

                <div className="relative mb-5 flex-1 border-t border-white/10 pt-4">
                    <ul className="space-y-2.5">
                        {t.pricing.features.team.map((feature, i) => (
                            <li key={i} className="flex items-start gap-2.5 text-xs font-medium text-white/90 leading-relaxed">
                                <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center text-prestige-300">
                                    <Check size={10} className="stroke-[3]" />
                                </div>
                                <span>{feature}</span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="relative mt-auto border-t border-white/10 pt-4">
                    <button
                      onClick={() => setShowContactModal(true)}
                      disabled={membershipInfo?.userLevel === 'team'}
                      className={`group/team h-11 w-full rounded-xl text-xs font-bold transition-all shadow-xl active:scale-[0.98] ${
                        membershipInfo?.userLevel === 'team'
                          ? 'bg-ink-dark text-white/50 cursor-default'
                          : 'bg-prestige-300 text-prestige-900 hover:bg-white'
                      }`}
                    >
                        <span className="flex items-center justify-center gap-2">
                            {membershipInfo?.userLevel === 'team' ? (
                              t.paymentModal.currentPlan
                            ) : (
                              <>
                                <Mail size={14} />
                                {t.paymentModal.contactTitle}
                                <ArrowRight size={16} className="transition-transform group-hover/team:translate-x-1 stroke-[2.5]" />
                              </>
                            )}
                        </span>
                    </button>
                </div>
            </div>
        </div>

        {/* Add-on Section (upgrade mode only) */}
        {!isWelcome && (
        <div className="max-w-5xl mx-auto">
            <div className="text-center mb-5">
                <h3 className="text-lg font-serif font-bold text-brand-900 mb-1">{t.pricing.addons.title}</h3>
                <p className="text-xs text-ink-secondary font-normal">{t.pricing.addons.desc}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">

                {/* Add-on 1: Single */}
                <div className="group flex items-center justify-between rounded-modal border border-surface-border bg-white/90 p-4 shadow-sm backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                    <div className="flex items-center gap-3">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-surface text-ink-muted transition-colors group-hover:bg-surface-hover group-hover:text-brand-900">
                            <Clock size={18} />
                        </div>
                        <div>
                            <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-ink-muted">
                                {t.pricing.addons.single}
                                {isCofounder && (
                                    <span className="ml-2 cofounder-badge relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-amber-300/70 overflow-hidden">
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" className="text-amber-700"><path d="M12 5l1.8 5h5.2l-4.2 3 1.6 4.8L12 15l-4.4 2.8 1.6-4.8L5 10h5.2L12 5z" fill="currentColor" opacity="0.85"/></svg>
                                        <span className="text-[8px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-800 bg-clip-text text-transparent">-{cofounderDiscount}%</span>
                                    </span>
                                )}
                            </div>
                            <div className="text-sm font-bold text-brand-900">
                                {regionCurrency}{isCofounder ? applyDiscount(regionPrices.addons.single) : regionPrices.addons.single}
                                {isCofounder && <span className="ml-1 text-[10px] text-ink-muted line-through">{regionCurrency}{regionPrices.addons.single}</span>}
                            </div>
                        </div>
                    </div>
                    <button
                      onClick={() => handlePurchaseClick('duration', '60min')}
                      disabled={purchasing === '60min'}
                      className="relative overflow-hidden group/add h-9 w-9 flex items-center justify-center rounded-lg bg-surface border border-surface-border text-brand-900 shadow-sm transition-all hover:bg-brand-900 hover:text-white hover:shadow-md hover:-translate-y-0.5 active:scale-95 disabled:opacity-50"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-0 group-hover/add:opacity-100 transition-opacity"></div>
                        {purchasing === '60min' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} strokeWidth={3} className="relative z-10" />}
                    </button>
                </div>

                {/* Add-on 2: Value Pack */}
                <div className="group relative flex items-center justify-between overflow-hidden rounded-modal border border-prestige-300/35 bg-prestige-50/70 p-4 shadow-sm backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                    <div className="absolute right-5 top-0 rounded-b-lg bg-prestige-900 px-2 py-0.5 text-[8px] font-black uppercase tracking-widest text-prestige-300">
                        {t.pricing.addons.popular}
                    </div>
                    <div className="flex items-center gap-3 pr-8">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-white text-prestige-700 shadow-sm">
                            <Coins size={18} />
                        </div>
                        <div>
                            <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-prestige-700">
                                {t.pricing.addons.value}
                                {isCofounder && (
                                    <span className="ml-2 cofounder-badge relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-amber-300/70 overflow-hidden">
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" className="text-amber-700"><path d="M12 5l1.8 5h5.2l-4.2 3 1.6 4.8L12 15l-4.4 2.8 1.6-4.8L5 10h5.2L12 5z" fill="currentColor" opacity="0.85"/></svg>
                                        <span className="text-[8px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-800 via-yellow-700 to-amber-800 bg-clip-text text-transparent">-{cofounderDiscount}%</span>
                                    </span>
                                )}
                            </div>
                            <div className="text-sm font-bold text-brand-900">
                                {regionCurrency}{isCofounder ? applyDiscount(regionPrices.addons.value.price) : regionPrices.addons.value.price}
                                <span className="ml-1 text-[10px] text-ink-muted line-through">{regionCurrency}{regionPrices.addons.value.originalPrice}</span>
                            </div>
                            <div className="mt-0.5 text-[10px] font-semibold text-prestige-700/75">{regionPrices.addons.value.detail}</div>
                        </div>
                    </div>
                    <button
                      onClick={() => handlePurchaseClick('duration', '600min')}
                      disabled={purchasing === '600min'}
                      className="relative overflow-hidden group/add h-9 w-9 flex items-center justify-center rounded-lg bg-prestige-900 text-prestige-300 shadow-cta-dark transition-all hover:-translate-y-0.5 hover:bg-prestige-800 hover:shadow-lg hover:text-white active:scale-95 disabled:opacity-50"
                    >
                        <div className="absolute inset-x-0 top-0 h-px bg-white/20"></div>
                        {purchasing === '600min' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} strokeWidth={3} className="relative z-10" />}
                    </button>
                </div>

                {/* Add-on 3: Business Pack */}
                <div className="group flex items-center justify-between rounded-modal border border-ink-dark bg-prestige-900 p-4 shadow-xl transition-all hover:-translate-y-0.5 hover:shadow-2xl">
                    <div className="flex items-center gap-3 text-white">
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/10 bg-white/10 text-prestige-300 backdrop-blur-sm">
                            <Clock size={18} />
                        </div>
                        <div>
                            <div className="mb-0.5 text-[10px] font-bold uppercase tracking-wider text-prestige-300/70">
                                {t.pricing.addons.business}
                                {isCofounder && (
                                    <span className="ml-2 relative inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md border border-prestige-300/40">
                                        <svg width="9" height="9" viewBox="0 0 24 24" fill="none" className="text-prestige-300"><path d="M12 5l1.8 5h5.2l-4.2 3 1.6 4.8L12 15l-4.4 2.8 1.6-4.8L5 10h5.2L12 5z" fill="currentColor" opacity="0.85"/></svg>
                                        <span className="text-[8px] font-black uppercase tracking-wider text-prestige-300">-{cofounderDiscount}%</span>
                                    </span>
                                )}
                            </div>
                            <div className="text-sm font-bold text-white">
                                {regionCurrency}{isCofounder ? applyDiscount(regionPrices.addons.business.price) : regionPrices.addons.business.price}
                                <span className="ml-1 text-[10px] text-white/30 line-through">{regionCurrency}{regionPrices.addons.business.originalPrice}</span>
                            </div>
                            <div className="mt-0.5 text-[10px] font-semibold text-prestige-300/70">{regionPrices.addons.business.detail}</div>
                        </div>
                    </div>
                    <button
                      onClick={() => handlePurchaseClick('duration', '3000min')}
                      disabled={purchasing === '3000min'}
                      className="relative overflow-hidden group/add h-9 w-9 flex items-center justify-center rounded-lg bg-prestige-300 text-prestige-900 shadow-md transition-all hover:-translate-y-0.5 hover:bg-white hover:shadow-lg active:scale-95 disabled:opacity-50"
                    >
                        <div className="absolute inset-x-0 top-0 h-px bg-white/40"></div>
                        {purchasing === '3000min' ? <Loader2 size={14} className="animate-spin" /> : <Plus size={16} strokeWidth={3} className="relative z-10" />}
                    </button>
                </div>

            </div>
        </div>
        )}

      </div>
      </div>{/* end scrollable */}
      </div>{/* end modal container */}

      {/* 地区检测 + 支付方式选择弹窗 */}
      {showRegionModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Payment Method" onKeyDown={(e) => { if (e.key === 'Escape') { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); } }}>
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
          ></div>
          <div className="relative w-full max-w-[460px] bg-white rounded-2xl shadow-2xl border border-surface-border/60 overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="absolute inset-0 bg-gradient-to-b from-surface/30 to-transparent pointer-events-none h-32"></div>

            <div className="relative px-8 pt-7 pb-8">
              {/* Header */}
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-brand-900">
                  {t.paymentModal.chooseMethod}
                </h3>
                <button
                  onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
                  className="p-1.5 -mr-1.5 text-ink-muted hover:text-brand-900 rounded-lg transition-colors hover:bg-surface"
                >
                  <X size={18} />
                </button>
              </div>

              {regionLoading ? (
                <div className="flex flex-col items-center gap-3 py-12">
                  <Loader2 size={28} className="animate-spin text-brand-700" />
                  <p className="text-sm text-ink-secondary">{t.paymentModal.detectingRegion}</p>
                </div>
              ) : regionInfo ? (
                <div className="space-y-5">
                  {/* Region pill */}
                  <div className="flex items-center gap-2 px-3.5 py-2 bg-surface rounded-lg border border-surface-border/60 w-fit">
                    <MapPin size={13} className="text-ink-muted" />
                    <span className="text-xs font-medium text-ink-secondary">
                      {regionInfo.region === 'CN' ? t.paymentModal.mainlandChina : t.paymentModal.overseas}
                      {regionInfo.city && ` · ${regionInfo.city}`}
                    </span>
                  </div>

                  {/* Payment options */}
                  <div className="space-y-3">
                    {regionInfo.availablePaymentMethods.filter(m => m === 'wechat' || m === 'alipay').map((m) => (
                      <button
                        key={m}
                        onClick={() => setSelectedPaymentMethod(m)}
                        className={`w-full flex items-center gap-4 px-5 py-4 rounded-xl border transition-all ${
                          selectedPaymentMethod === m
                            ? 'border-brand-700 bg-brand-50/60 shadow-sm'
                            : 'border-surface-border bg-white hover:border-brand-200 hover:bg-surface/30'
                        }`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                          m === 'wechat' ? 'bg-wechat/10' : 'bg-alipay/10'
                        }`}>
                          {m === 'wechat' ? (
                            <WeChatPayIcon size={24} />
                          ) : (
                            <AlipayIcon size={24} />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="text-sm font-bold text-brand-900 leading-tight">
                            {m === 'wechat' ? t.paymentModal.wechatPay : t.paymentModal.alipay}
                          </div>
                          <div className="text-xs text-ink-muted mt-0.5">
                            {m === 'wechat' ? t.paymentModal.scanToPay : t.paymentModal.redirectToPay}
                          </div>
                        </div>
                        <div className={`w-5 h-5 rounded-full border-[1.5px] flex items-center justify-center transition-all ${
                          selectedPaymentMethod === m ? 'border-brand-700 bg-brand-700' : 'border-surface-border'
                        }`}>
                          {selectedPaymentMethod === m && <Check size={12} className="text-white" strokeWidth={3} />}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-10 text-sm text-ink-secondary">
                  {t.paymentModal.detectFailed}
                </div>
              )}

              {/* Actions */}
              <div className="mt-6 flex gap-3">
                <button
                  onClick={() => { setShowRegionModal(false); setPendingPurchase(null); setSelectedPaymentMethod(null); }}
                  className="flex-1 h-11 rounded-xl text-sm font-bold text-ink-secondary bg-surface border border-surface-border hover:bg-surface-hover transition-all"
                >
                  {t.paymentModal.cancel}
                </button>
                <button
                  onClick={handleRegionConfirm}
                  disabled={regionLoading || !selectedPaymentMethod || !!purchasing}
                  className="flex-[2] h-11 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98] disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {regionLoading
                    ? t.paymentModal.detecting
                    : t.paymentModal.confirmPayment}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 二维码支付弹窗 */}
      {showQrModal && qrCodeUrl && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="QR Code Payment" onKeyDown={(e) => { if (e.key === 'Escape') setShowQrModal(false); }}>
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setShowQrModal(false)}
          ></div>
          <div className="relative w-full max-w-[420px] bg-white rounded-2xl shadow-2xl border border-surface-border/60 overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="relative px-8 pt-7 pb-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                    qrPaymentType?.includes('wechat') ? 'bg-wechat/10' : 'bg-alipay/10'
                  }`}>
                    {qrPaymentType?.includes('wechat') ? (
                      <WeChatPayIcon size={20} />
                    ) : (
                      <AlipayIcon size={20} />
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-brand-900">
                    {qrPaymentType?.includes('wechat')
                      ? t.paymentModal.wechatScanPay
                      : t.paymentModal.alipayScanPay}
                  </h3>
                </div>
                <button
                  onClick={() => setShowQrModal(false)}
                  className="p-1.5 -mr-1.5 text-ink-muted hover:text-brand-900 rounded-lg transition-colors hover:bg-surface"
                >
                  <X size={18} />
                </button>
              </div>

              <p className="text-xs text-ink-muted text-center mb-5">
                {t.paymentModal.qrScanScan}
              </p>

              {/* H-2: Local QR code generation via canvas */}
              <div className="flex justify-center mb-5">
                <div className="bg-white border border-surface-border rounded-xl p-3.5 shadow-sm">
                  <QRCodeCanvas
                    value={qrCodeUrl}
                    size={200}
                    marginSize={0}
                  />
                </div>
              </div>

              {qrOrderNo && (
                <p className="text-[11px] text-ink-muted text-center mb-3">
                  {t.paymentModal.qrOrderNo}{qrOrderNo}
                </p>
              )}

              <p className="text-xs text-ink-secondary text-center mb-6">
                {t.paymentModal.qrUpdateAuto}
              </p>

              <button
                onClick={async () => {
                  setShowQrModal(false);
                  setQrCodeUrl(null);
                  await loadMembershipInfo();
                }}
                className="w-full h-11 rounded-xl font-bold text-sm bg-brand-900 text-white hover:bg-brand-800 transition-all active:scale-[0.98]"
              >
                {t.paymentModal.qrFinished}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Team 联系我们弹窗 */}
      {showContactModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Contact" onKeyDown={(e) => { if (e.key === 'Escape') setShowContactModal(false); }}>
          <div
            className="absolute inset-0 bg-black/50 backdrop-blur-sm"
            onClick={() => setShowContactModal(false)}
          ></div>
          <div className="relative w-full max-w-md bg-surface-light rounded-modal shadow-2xl border border-surface-border overflow-hidden animate-in zoom-in-95 duration-300">
            <div className="absolute inset-0 bg-noise opacity-[0.03] pointer-events-none"></div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-prestige-300/10 rounded-bl-[120px] pointer-events-none"></div>
            <button
              onClick={() => setShowContactModal(false)}
              className="absolute top-6 right-6 z-20 p-2 text-ink-muted hover:text-brand-900 rounded-full transition-colors hover:bg-surface"
            >
              <X size={20} />
            </button>
            <div className="p-10 relative z-10">
              <div className="w-16 h-16 bg-prestige-900 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                <Mail size={28} className="text-prestige-300" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-brand-900 text-center mb-2">
                {t.paymentModal.contactTitle}
              </h3>
              <p className="text-sm text-ink-secondary text-center mb-8 leading-relaxed">
                {t.paymentModal.contactDesc}
              </p>
              <div className="bg-surface rounded-2xl border border-surface-border p-5 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-10 h-10 bg-white rounded-xl border border-surface-border flex items-center justify-center shrink-0 shadow-sm">
                    <Mail size={18} className="text-ink-secondary" />
                  </div>
                  <span className="text-sm font-mono font-bold text-brand-900 truncate">support@minutes-ai.com</span>
                </div>
                <button
                  onClick={() => {
                    copyToClipboard('support@minutes-ai.com');
                    setCopied(true);
                    setTimeout(() => setCopied(false), 2000);
                  }}
                  className={`shrink-0 px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider transition-all ${
                    copied ? 'bg-brand-500 text-white' : 'bg-brand-900 text-white hover:bg-brand-800'
                  }`}
                >
                  {copied ? t.paymentModal.copied : t.paymentModal.copy}
                </button>
              </div>
              <button
                onClick={() => setShowContactModal(false)}
                className="w-full mt-6 py-3 rounded-xl text-sm font-bold text-ink-secondary bg-white border border-surface-border hover:bg-surface transition-all"
              >
                {t.paymentModal.close}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 购买状态提示 - 置于最外层确保在所有弹窗之上 */}
      {(purchaseError || purchaseSuccess || purchaseInfo) && (
        <div className="fixed top-20 right-4 z-[9999] max-w-md animate-in slide-in-from-top-2 duration-300">
          {purchaseError && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-4 shadow-lg flex items-start gap-3">
              <AlertCircle size={20} className="text-red-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold text-red-700">{t.paymentModal.purchaseFailed}</div>
                <div className="text-xs text-red-600 mt-1">{purchaseError}</div>
              </div>
              <button onClick={() => setPurchaseError(null)} className="text-red-400 hover:text-red-600">×</button>
            </div>
          )}
          {purchaseSuccess && (
            <div className="bg-green-50 border border-green-200 rounded-2xl p-4 shadow-lg flex items-start gap-3">
              <Check size={20} className="text-green-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold text-green-700">{t.paymentModal.purchaseSuccess}</div>
                <div className="text-xs text-green-600 mt-1">{purchaseSuccess}</div>
              </div>
              <button onClick={() => setPurchaseSuccess(null)} className="text-green-400 hover:text-green-600">×</button>
            </div>
          )}
          {/* H-3: info-level toast for pending payment (not confirmed success) */}
          {purchaseInfo && (
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 shadow-lg flex items-start gap-3 mt-2">
              <AlertCircle size={20} className="text-blue-500 shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-bold text-blue-700">{t.paymentModal.processing}</div>
                <div className="text-xs text-blue-600 mt-1">{purchaseInfo}</div>
              </div>
              <button onClick={() => setPurchaseInfo(null)} className="text-blue-400 hover:text-blue-600">×</button>
            </div>
          )}
        </div>
      )}

    </div>
  );
}
